// Drawer.tsx
import Offcanvas from 'react-bootstrap/Offcanvas';
import Accordion from 'react-bootstrap/Accordion';
import Form from 'react-bootstrap/Form';

import { useVizStore } from '@/store';
import type { MetricColorScheme } from '@/types';

export function Drawer() {
  // Use individual selectors for viz store values to avoid object comparison issues
  const drawerOpen = useVizStore((s) => s.drawerOpen);
  const bins = useVizStore((s) => s.binNum);
  const useBins = useVizStore((s) => s.useBins);
  const points = useVizStore((s) => s.showPoints);
  const polyColor = useVizStore((s) => s.polyColor);
  const pointsColor = useVizStore((s) => s.pointsColor);
  const metricColorScheme = useVizStore((s) => s.metricColorScheme);
  const voteSupportColor = useVizStore((s) => s.voteSupportColor);
  const voteAgainstColor = useVizStore((s) => s.voteAgainstColor);
  const voteAbsenceColor = useVizStore((s) => s.voteAbsenceColor);
  const voteNoDataColor = useVizStore((s) => s.voteNoDataColor);
  const useFeatureLabel = useVizStore((s) => s.useFeatureLabel);

  // Get functions separately - they're stable references
  const closeDrawer = useVizStore((s) => s.closeDrawer);
  const setBins = useVizStore((s) => s.setBinNum);
  const setUseBins = useVizStore((s) => s.setUseBins);
  const setPoints = useVizStore((s) => s.setShowPoints);
  const setPolyColor = useVizStore((s) => s.setPolyColor);
  const setPointsColor = useVizStore((s) => s.setPointsColor);
  const setMetricColorScheme = useVizStore((s) => s.setMetricColorScheme);
  const setVoteSupportColor = useVizStore((s) => s.setVoteSupportColor);
  const setVoteAgainstColor = useVizStore((s) => s.setVoteAgainstColor);
  const setVoteAbsenceColor = useVizStore((s) => s.setVoteAbsenceColor);
  const setVoteNoDataColor = useVizStore((s) => s.setVoteNoDataColor);
  const setUseFeatureLabel = useVizStore((s) => s.setUseFeatureLabel);

  return (
    <Offcanvas placement="start" show={drawerOpen} onHide={closeDrawer}>
      <Offcanvas.Header closeButton>
        <Offcanvas.Title>Visualization Options</Offcanvas.Title>
      </Offcanvas.Header>

      <Offcanvas.Body>
        <Accordion defaultActiveKey={['data', 'map']} alwaysOpen>
          {/* MAP */}
          <Accordion.Item eventKey="map">
            <Accordion.Header>Map</Accordion.Header>
            <Accordion.Body>
              <Form>
                {/* Bins Toggle */}
                <div className="row mb-3">
                  <Form.Label className="col-4 col-form-label fw-semibold mb-0">Bins</Form.Label>
                  <div className="col-8 d-flex flex-column gap-1">
                    <Form.Check
                      type="checkbox"
                      id="bins-toggle"
                      label="Use bins"
                      checked={useBins}
                      onChange={(e) => setUseBins(e.target.checked)}
                    />
                  </div>
                </div>

                {/* Bins Range */}
                <div className={`row align-items-center mb-3 ${!useBins ? 'opacity-50' : ''}`}>
                  <Form.Label
                    htmlFor="bins-range"
                    className="col-4 col-form-label fw-semibold mb-0"
                  >
                    Bins: {bins}
                  </Form.Label>
                  <div className="col-8">
                    <Form.Range
                      id="bins-range"
                      min={3}
                      max={9}
                      value={bins}
                      onChange={(e) => setBins(Number(e.currentTarget.value))}
                      disabled={!useBins}
                      aria-label="Bins"
                    />
                  </div>
                </div>

                {/* Layers */}
                <div className="row mb-3">
                  <Form.Label className="col-4 col-form-label fw-semibold mb-0">Layers</Form.Label>
                  <div className="col-8 d-flex flex-column gap-1">
                    <Form.Check
                      type="checkbox"
                      id="layer-points"
                      label="Points"
                      checked={points}
                      onChange={(e) => setPoints(e.target.checked)}
                    />
                  </div>
                </div>

                {/* Tooltip Label */}
                <div className="row mb-3">
                  <Form.Label className="col-4 col-form-label fw-semibold mb-0">
                    Compress Tooltip
                  </Form.Label>
                  <div className="col-8 d-flex flex-column gap-1">
                    <Form.Check
                      type="switch"
                      id="feature-label-toggle"
                      label="sets metric label in tooltip to 'Feature'"
                      checked={useFeatureLabel}
                      onChange={(e) => setUseFeatureLabel(e.target.checked)}
                    />
                  </div>
                </div>

                {/* Color Options */}
                <div className="row align-items-center mb-3">
                  <Form.Label
                    htmlFor="select-poly-color"
                    className="col-4 col-form-label fw-semibold mb-0"
                  >
                    Polygon Color
                  </Form.Label>
                  <div className="col-8">
                    <Form.Select
                      id="select-poly-color"
                      size="sm"
                      value={polyColor}
                      onChange={(e) => setPolyColor(e.target.value as 'voteType' | 'metric')}
                    >
                      <option value="voteType">Vote Type</option>
                      <option value="metric">Map Feature</option>
                    </Form.Select>
                  </div>
                </div>

                <div className="row align-items-center mb-3">
                  <Form.Label
                    htmlFor="select-points-color"
                    className="col-4 col-form-label fw-semibold mb-0"
                  >
                    Points Color
                  </Form.Label>
                  <div className="col-8">
                    <Form.Select
                      id="select-points-color"
                      size="sm"
                      value={points ? pointsColor : 'none'}
                      onChange={(e) => {
                        if (e.target.value !== 'none') {
                          setPointsColor(e.target.value as 'voteType' | 'metric');
                        }
                      }}
                      disabled={!points}
                    >
                      <option value="none">None</option>
                      <option value="voteType">Vote Type</option>
                      <option value="metric">Metric</option>
                    </Form.Select>
                  </div>
                </div>

                {/* Metric Color Scheme */}
                <div className="row align-items-center mb-3">
                  <Form.Label
                    htmlFor="select-metric-color-scheme"
                    className="col-4 col-form-label fw-semibold mb-0"
                  >
                    Map Feature Scheme
                  </Form.Label>
                  <div className="col-8">
                    <Form.Select
                      id="select-metric-color-scheme"
                      size="sm"
                      value={metricColorScheme}
                      onChange={(e) => setMetricColorScheme(e.target.value as MetricColorScheme)}
                      disabled={polyColor !== 'metric' && pointsColor !== 'metric'}
                    >
                      <option value="Blues">Blues</option>
                      <option value="Greens">Greens</option>
                      <option value="Oranges">Oranges</option>
                      <option value="Purples">Purples</option>
                      <option value="Reds">Reds</option>
                      <option value="Greys">Greys</option>
                    </Form.Select>
                  </div>
                </div>

                {/* Vote Colors */}
                <div className="row mb-2">
                  <Form.Label className="col-4 col-form-label fw-semibold mb-2">
                    Vote Colors
                  </Form.Label>
                  <div className="col-8 d-flex flex-column gap-2">
                    <div className="row align-items-center">
                      <Form.Label
                        htmlFor="vote-support-color"
                        className="col-5 col-form-label mb-0 small"
                      >
                        Support
                      </Form.Label>
                      <div className="col-7">
                        <Form.Control
                          type="text"
                          id="vote-support-color"
                          value={voteSupportColor}
                          onChange={(e) => setVoteSupportColor(e.target.value)}
                          disabled={!points && polyColor === 'metric'}
                          size="sm"
                          pattern="^#[0-9A-Fa-f]{6}$"
                          placeholder="#80CFA9"
                        />
                      </div>
                    </div>
                    <div className="row align-items-center">
                      <Form.Label
                        htmlFor="vote-against-color"
                        className="col-5 col-form-label mb-0 small"
                      >
                        Against
                      </Form.Label>
                      <div className="col-7">
                        <Form.Control
                          type="text"
                          id="vote-against-color"
                          value={voteAgainstColor}
                          onChange={(e) => setVoteAgainstColor(e.target.value)}
                          disabled={!points && polyColor === 'metric'}
                          size="sm"
                          pattern="^#[0-9A-Fa-f]{6}$"
                          placeholder="#cccc1b"
                        />
                      </div>
                    </div>
                    <div className="row align-items-center">
                      <Form.Label
                        htmlFor="vote-absence-color"
                        className="col-5 col-form-label mb-0 small"
                      >
                        Absence
                      </Form.Label>
                      <div className="col-7">
                        <Form.Control
                          type="text"
                          id="vote-absence-color"
                          value={voteAbsenceColor}
                          onChange={(e) => setVoteAbsenceColor(e.target.value)}
                          disabled={!points && polyColor === 'metric'}
                          size="sm"
                          pattern="^#[0-9A-Fa-f]{6}$"
                          placeholder="#EEEEEE"
                        />
                      </div>
                    </div>
                    <div className="row align-items-center">
                      <Form.Label
                        htmlFor="vote-no-data-color"
                        className="col-5 col-form-label mb-0 small"
                      >
                        No Data
                      </Form.Label>
                      <div className="col-7">
                        <Form.Control
                          type="text"
                          id="vote-no-data-color"
                          value={voteNoDataColor}
                          onChange={(e) => setVoteNoDataColor(e.target.value)}
                          disabled={!points && polyColor === 'metric'}
                          size="sm"
                          pattern="^#[0-9A-Fa-f]{6}$"
                          placeholder="#CCCCCC"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </Form>
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
      </Offcanvas.Body>
    </Offcanvas>
  );
}
